import * as React from 'react'

import {
  FolderOpenIcon,
  MailIcon,
  MapPinIcon,
  PhoneCallIcon,
  SettingsIcon,
} from 'lucide-react'
import { inject, observer } from 'mobx-react'

import { IProject, ISharedSitePermit } from '~/client/graph'
import { GenericWorkflowIcon } from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowIconGroups'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { getSharedProjectName } from '../../models/SharedInfo'
import Workflow from '../../models/Workflow'
import ProjectsStore from '../../stores/domain/Projects.store'
import InitialState from '../../stores/InitialState'
import {
  ISharedAction,
  isPhoneValid,
  renderActionItem,
  SharedEntityActions,
  SharedEntityActionTypes,
} from './SharedEntityActions'

import './SharedEntityActions.scss'

interface IProps {
  entity: ISharedSitePermit
  projectId: string
  companyName: string
  onActionClick: (
    actionType: SharedEntityActionTypes,
    sharedEntity: ISharedSitePermit,
    e: React.MouseEvent,
  ) => void
  isMobile?: boolean
  projectsStore?: ProjectsStore
  state?: InitialState
}

// eslint-disable-next-line
@inject('projectsStore', 'state')
@observer
export default class SharedPermitActions extends React.Component<IProps> {
  private get permit(): Workflow {
    return Workflow.fromSharedPermitDto(this.props.entity)
  }

  private get project(): IProject {
    const { projectsStore, projectId } = this.props
    return projectsStore.getProjectById(projectId)
  }

  public render(): JSX.Element {
    const {
      entity,
      projectId,
      companyName,
      onActionClick,
      isMobile,
      projectsStore,
      state,
    } = this.props

    return (
      <SharedEntityActions
        entity={entity}
        projectId={projectId}
        companyName={companyName}
        onActionClick={onActionClick}
        isMobile={isMobile}
        projectsStore={projectsStore}
        state={state}
        renderActions={this.renderActions}
        renderInfo={this.renderInfo}
        projectName={
          this.project?.name || getSharedProjectName(entity.projectUrl)
        }
      />
    )
  }

  private renderActions = (): JSX.Element => {
    const { entity: permit, isMobile, state, onActionClick } = this.props
    const { isAdmin } = state.userActiveProjectSettings

    const actions: ISharedAction[] = [
      {
        type: SharedEntityActionTypes.GO_TO_PROJECT,
        label: Localization.translator.goToTheProject,
        icon: <FolderOpenIcon size={20} className="tw:text-muted-foreground" />,
      },
    ]

    if (permit.locationUrl) {
      actions.push({
        type: SharedEntityActionTypes.GO_TO_TO_LOCATION,
        label: Localization.translator.goToLocation(permit.locationName),
        icon: <MapPinIcon size={16} />,
      })
    }

    actions.push(
      {
        type: SharedEntityActionTypes.CALL_ADMIN,
        label: Localization.translator.callTo(permit.responsible.firstName),
        icon: <PhoneCallIcon size={20} className="tw:text-muted-foreground" />,
        disabled: !isPhoneValid(permit.responsible.phoneNumber),
      },
      {
        type: SharedEntityActionTypes.MAIL_ADMIN,
        label: Localization.translator.mailTo(permit.responsible.firstName),
        icon: <MailIcon size={20} className="tw:text-muted-foreground" />,
        disabled: !permit.responsible.email,
      },
    )

    if (!isMobile && isAdmin) {
      actions.push({
        type: SharedEntityActionTypes.GO_TO_SETTINGS,
        label: Localization.translator.iCalSharingSettings,
        icon: <SettingsIcon size={20} className="tw:text-muted-foreground" />,
      })
    }

    return (
      <div className="shared-actions-list col">
        {actions.map(action => renderActionItem(action, onActionClick, permit))}
      </div>
    )
  }

  private renderInfo = (): JSX.Element => {
    return (
      <div className="delivery-info row pa10 y-start">
        <div className="col y-start no-grow">
          <div className="delivery-workflow-card-icon bg-dark br-rounded text white row x-center y-center no-flex mr20">
            <GenericWorkflowIcon size={16} className="tw:flex-none!" />
          </div>
        </div>
        <div className="col">
          <div className="row y-stretch">
            <span className="col row delivery-company-title text extra-large bold ellipsis">
              {this.props.companyName}
              <span className="col ml5 text light pa2 pt4">
                #{this.permit.title}
              </span>
            </span>
          </div>
        </div>
      </div>
    )
  }
}
